import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faVideo, 
  faMusic, 
  faImage, 
  faUpload, 
  faPlus, 
  faSpinner,
  faImages,
  faPlay
} from '@fortawesome/free-solid-svg-icons';
import styles from '../ManagerDashboard/Profile.module.css';
import UserContext from '../../context/UserContext';
import { validatePostUrl } from '../../utils/actions/PostActions';
import AudioTrimmerModal from '../common/AudioTrimmerModal';
import VideoTrimmerModal from '../common/VideoTrimmerModal';


// Upgrade Modal Component
const UpgradeModal = ({ isOpen, onClose, navigate }) => {
  if (!isOpen) return null;
  
  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Upgrade to Elite</h2>
        <p>The ability to add links to your posts is available exclusively to Elite members.</p>
        <div className={styles['form-actions']}>
          <button 
            className={styles['secondary-button']} 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className={styles['primary-button']} 
            onClick={() => {
              onClose();
              navigate('/membership');
            }}
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUpload, existingTags, editingPost }) => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);

  // Add a safe check for the post type
  const getInitialPostType = () => {
    if (editingPost?.type) {
      return editingPost.type;
    }
    return null;
  };

  const [postType, setPostType] = useState(getInitialPostType());
  const [url, setUrl] = useState(editingPost?.url || '');
  const [caption, setCaption] = useState(editingPost?.caption || '');
  const [tags, setTags] = useState(editingPost?.tags?.join(', ') || '');
  const [selectedExistingTags, setSelectedExistingTags] = useState(
    editingPost?.tags || []
  );

  const [link, setLink] = useState(editingPost?.link || '');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Media upload states
  const [uploadMethod, setUploadMethod] = useState(editingPost?.isEmbed ? 'embed' : 'upload');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [videoThumbnailTime, setVideoThumbnailTime] = useState(50);
  const [isCarousel, setIsCarousel] = useState(false);
  
  // OEmbed states
  const [embedData, setEmbedData] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState('');
  
  // Processing states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  
  const [audioCoverImage, setAudioCoverImage] = useState(null);
  const audioCoverInputRef = useRef(null);

  const [mediaDuration, setMediaDuration] = useState(0);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const carouselInputRef = useRef(null);
  
  // Context
  const { authToken } = useContext(UserContext);

  const [localTrimStart, setLocalTrimStart] = useState(trimStart);
  const [localTrimEnd, setLocalTrimEnd] = useState(trimEnd);

  // Add this effect to handle cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (audioCoverImage?.previewUrl) {
        URL.revokeObjectURL(audioCoverImage.previewUrl);
      }
    };
  }, [audioCoverImage]);

  // Add this helper function to determine max duration based on membership tier
  const getMaxDuration = (mediaType, membershipTier) => {
    const durations = {
      video: {
        'Free': 30, // 30 seconds
        'Basic': 60, // 1 minute
        'Pro': 120, // 2 minutes
        'Elite': 300 // 5 minutes
      },
      audio: {
        'Free': 300, // 5 minutes
        'Basic': 600, // 10 minutes
        'Pro': 900, // 15 minutes
        'Elite': 1200 // 20 minutes
      }
    };
    
    return durations[mediaType]?.[membershipTier] || durations[mediaType]?.['Free'];
  };

  // Duration Limit Modal Component
const DurationLimitModal = ({ isOpen, onClose, mediaType, duration, maxDuration }) => {
    if (!isOpen) return null;
    
    // Format seconds to minutes and seconds for display
    const formatDuration = (seconds) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      if (minutes > 0) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds > 0 ? `${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`;
      }
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    };
    
    return (
      <div className={styles['modal-overlay']}>
        <div className={styles['modal-content']}>
          <button className={styles['modal-close']} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <h2>Duration Limit</h2>
          <p>
            Your {mediaType} is {formatDuration(duration)} long, but your membership allows up to {formatDuration(maxDuration)}.
          </p>
          <p>You can:</p>
          <ul>
            <li>Trim your {mediaType} to {formatDuration(maxDuration)}</li>
            <li>Upgrade your membership to upload longer {mediaType}s</li>
          </ul>
          
          <div className={styles['membership-limits']}>
            <h3>Membership Duration Limits</h3>
            <div className={styles['limits-table']}>
              <div className={styles['limit-row']}>
                <div className={styles['limit-cell']}>Free</div>
                <div className={styles['limit-cell']}>
                  Videos: 30 seconds<br/>
                  Audio: 5 minutes
                </div>
              </div>
              <div className={styles['limit-row']}>
                <div className={styles['limit-cell']}>Basic</div>
                <div className={styles['limit-cell']}>
                  Videos: 1 minute<br/>
                  Audio: 10 minutes
                </div>
              </div>
              <div className={styles['limit-row']}>
                <div className={styles['limit-cell']}>Pro</div>
                <div className={styles['limit-cell']}>
                  Videos: 2 minutes<br/>
                  Audio: 15 minutes
                </div>
              </div>
              <div className={styles['limit-row']}>
                <div className={styles['limit-cell']}>Elite</div>
                <div className={styles['limit-cell']}>
                  Videos: 5 minutes<br/>
                  Audio: 20 minutes
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles['form-actions']}>
            <button 
              className={styles['secondary-button']} 
              onClick={() => {
                onClose();
                setShowTrimmer(true);
              }}
            >
              Trim {mediaType}
            </button>
            <button 
              className={styles['primary-button']} 
              onClick={() => {
                onClose();
                navigate('/membership');
              }}
            >
              Upgrade Membership
            </button>
          </div>
        </div>
      </div>
    );
  };
  

  useEffect(() => {
    if (editingPost) {
      // Only set values if editingPost has the required properties
      if (editingPost.type) {
        setPostType(editingPost.type);
      }
      setUrl(editingPost.url || '');
      setCaption(editingPost.caption || '');
      setTags(editingPost.tags?.join(', ') || '');
      setSelectedExistingTags(editingPost.tags || []);
      
      // Set upload method based on post properties
      setUploadMethod(editingPost.isEmbed ? 'embed' : 'upload');
      
      // Set carousel state if applicable
      setIsCarousel(editingPost.isCarousel || false);
      
      // Set embed data if applicable
      if (editingPost.isEmbed) {
        setEmbedData({
          html: editingPost.embedHtml,
          thumbnail_url: editingPost.thumbnailUrl,
          title: editingPost.title,
          author_name: editingPost.authorName,
          provider_name: editingPost.providerName,
          url: editingPost.url
        });
      }
    }
  }, [editingPost]);

  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      mediaFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, [mediaFiles]);

  // Add this function to handle cover image selection
  const handleAudioCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Revoke previous URL if exists
    if (audioCoverImage?.previewUrl) {
      URL.revokeObjectURL(audioCoverImage.previewUrl);
    }
    
    // Create a preview URL
    const previewUrl = URL.createObjectURL(file);
    
    setAudioCoverImage({
      file,
      previewUrl,
      name: file.name,
      type: file.type,
      size: file.size
    });
  };

  // Add this function to trigger cover image input
  const handleAudioCoverInputClick = () => {
    audioCoverInputRef.current.click();
  };

  // Add this function to remove cover image
  const handleRemoveAudioCover = () => {
    if (audioCoverImage?.previewUrl) {
      URL.revokeObjectURL(audioCoverImage.previewUrl);
    }
    setAudioCoverImage(null);
  };

  const handleTagClick = (tag) => {
    const normalizedTag = tag.toLowerCase().replace(/\s+/g, '');
    const updatedTags = selectedExistingTags.includes(normalizedTag)
      ? selectedExistingTags.filter(t => t !== normalizedTag)
      : [...selectedExistingTags, normalizedTag];
    setSelectedExistingTags(updatedTags);
    setTags(updatedTags.join(', '));
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setIsUploading(true);
    
    try {
      if (selectedFile.type.startsWith('video/') || selectedFile.type.startsWith('audio/')) {
        const mediaType = selectedFile.type.startsWith('video/') ? 'video' : 'audio';
        const maxDuration = getMaxDuration(mediaType, userData?.membershipTier || 'Free');
        
        const mediaElement = document.createElement(mediaType);
        mediaElement.preload = 'metadata';
        
        await new Promise((resolve, reject) => {
          mediaElement.onloadedmetadata = () => {
            const duration = Math.floor(mediaElement.duration);
            setMediaDuration(duration);
            
            // First add the file to the files array
            handleAddFile(selectedFile);
            
            // Then check if trimming is needed
            if (duration > maxDuration) {
              setTrimStart(0);
              setTrimEnd(Math.min(duration, maxDuration));
              setLocalTrimStart(0);
              setLocalTrimEnd(Math.min(duration, maxDuration));
              // Show trimmer modal after file is added
              setTimeout(() => setShowTrimmer(true), 100);
            }
            
            resolve();
            URL.revokeObjectURL(mediaElement.src);
          };
          
          mediaElement.onerror = reject;
          mediaElement.src = URL.createObjectURL(selectedFile);
        });
      } else {
        handleAddFile(selectedFile);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setUploadError(`Error processing file: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const checkMediaDuration = (file) => {
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      const element = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
      element.preload = 'metadata';
      
      element.onloadedmetadata = () => {
        const duration = Math.round(element.duration);
        setMediaDuration(duration);
        
        // Check if duration exceeds limit based on membership tier
        const mediaType = file.type.startsWith('video/') ? 'video' : 'audio';
        const maxDuration = getMaxDuration(mediaType, userData.membershipTier || 'Free');
        
        if (duration > maxDuration) {
          // Set default trim values
          setTrimStart(0);
          setTrimEnd(maxDuration);
          setShowTrimmer(true);
        } else {
          // Reset trimmer state
          setShowTrimmer(false);
          setTrimStart(0);
          setTrimEnd(duration);
        }
      };
      
      element.onerror = () => {
        console.error('Error loading media metadata');
        setMediaDuration(0);
      };
      
      element.src = URL.createObjectURL(file);
    }
  };
  
  const handleAddFile = (file) => {
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    const newFile = {
      file,
      previewUrl,
      name: file.name,
      type: file.type,
      size: file.size
    };
    
    // If it's the first file in carousel or not a carousel, set as current
    if (mediaFiles.length === 0) {
      setCurrentFileIndex(0);
      
      // Auto-detect post type from file type
      if (file.type.startsWith('image/')) {
        setPostType('photo');
      } else if (file.type.startsWith('video/')) {
        setPostType('video');
      } else if (file.type.startsWith('audio/')) {
        setPostType('audio');
      }
    }
    
    setMediaFiles(prev => [...prev, newFile]);
  
    // Use the file parameter instead of selectedFile
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      checkMediaDuration(file);
    }
  };
  
  const handleRemoveFile = (index) => {
    setMediaFiles(prev => {
      // Revoke object URL to prevent memory leaks
      if (prev[index]?.previewUrl) {
        URL.revokeObjectURL(prev[index].previewUrl);
      }
      
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      
      // Update current index if needed
      if (currentFileIndex >= newFiles.length && newFiles.length > 0) {
        setCurrentFileIndex(newFiles.length - 1);
      } else if (newFiles.length === 0) {
        setCurrentFileIndex(0);
      }
      
      return newFiles;
    });
  };
  
  const handleCarouselFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    // Check file types - only allow images for carousel
    const validFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length === 0) {
      setUploadError('Only image files are allowed for carousel');
      return;
    }
    
    // Enable carousel mode if adding multiple files
    if (validFiles.length > 0 || mediaFiles.length > 0) {
      setIsCarousel(true);
    }
    
    // Add all valid files
    validFiles.forEach(file => handleAddFile(file));
  };
  
  const fetchOEmbedData = async () => {
    if (!url) {
      setEmbedError('Please enter a URL');
      return;
    }
    
    setEmbedLoading(true);
    setEmbedError('');
    setEmbedData(null);
    
    try {
      const response = await fetch(`/api/fetchOEmbedData?url=${encodeURIComponent(url)}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch embed data');
      }
      
      const data = await response.json();
      
      // Log the full data response for debugging
      console.log('OEmbed data received:', data);
      
      // Ensure all properties exist, even if null, to avoid undefined errors
      const cleanData = {
        html: data.html || null,
        thumbnail_url: data.thumbnail_url || null,
        title: data.title || null,
        author_name: data.author_name || null,
        author_url: data.author_url || null,
        provider_name: data.provider_name || null,
        provider_url: data.provider_url || null,
        width: data.width || null,
        height: data.height || null,
        // Ensure we always have a URL
        url: data.url || url
      };
      
      setEmbedData(cleanData);
      
      // Auto-detect post type from provider name
      if (data.provider_name === 'Spotify' || data.provider_name === 'SoundCloud') {
        setPostType('audio');
      }
    } catch (error) {
      console.error('Error fetching OEmbed data:', error);
      setEmbedError(error.message || 'Failed to fetch embed data');
    } finally {
      setEmbedLoading(false);
    }
  };
  
  const handleChunkedUpload = async (file) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError('');
      
      // Generate a unique file ID
      const fileId = `file_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // Chunk size: 5MB
      const CHUNK_SIZE = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      
      // Check if we have an audio cover image to include
      let coverImageData = null;
      if (postType === 'audio' && audioCoverImage) {
        // Convert cover image to base64
        try {
          const reader = new FileReader();
          coverImageData = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(audioCoverImage.file);
          });
        } catch (error) {
          console.warn('Error reading cover image:', error);
        }
      }
      
      // Prepare file info including cover image data if present
      const fileInfo = {
        fileId,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        videoTimestamp: postType === 'video' ? `${videoThumbnailTime}%` : '50%',
        coverImageData: coverImageData
      };
      
      // Upload each chunk
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        // Calculate chunk boundaries
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        
        // Create a chunk blob
        const chunk = file.slice(start, end);
        
        // Prepare chunk info
        const chunkInfo = {
          index: chunkIndex,
          total: totalChunks,
          size: chunk.size
        };
        
        // Update progress
        const progressPercent = Math.round((chunkIndex / totalChunks) * 90);
        setUploadProgress(progressPercent);
        
        // Upload this chunk
        const response = await fetch('/api/uploadFileChunk', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/octet-stream',
            'X-File-Info': JSON.stringify(fileInfo),
            'X-Chunk-Info': JSON.stringify(chunkInfo)
          },
          body: chunk
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload chunk');
        }
        
        const data = await response.json();
        
        // If this was the last chunk and processing is complete
        if (data.complete) {
          setUploadProgress(100);
          
          // Return the file info
          return {
            originalName: file.name,
            fileName: data.fileInfo.fileName,
            thumbnailName: data.fileInfo.thumbnailPath ? data.fileInfo.thumbnailPath.split('/').pop() : null,
            contentType: file.type,
            type: file.type.startsWith('image/') ? 'photo' : 
                  file.type.startsWith('video/') ? 'video' : 
                  file.type.startsWith('audio/') ? 'audio' : 'file',
            isMain: true,
            url: data.fileInfo.url,
            thumbnailUrl: data.fileInfo.thumbnailUrl || null,
            filePath: data.fileInfo.filePath,
            thumbnailPath: data.fileInfo.thumbnailPath || null
          };
        }
      }
      
      // If we get here, something went wrong
      throw new Error('Upload incomplete');
      
    } catch (error) {
      console.error('Error with chunked upload:', error);
      setUploadError(error.message || 'Failed to upload file');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const processAndUploadMedia = async (extraData = {}) => {
    if (mediaFiles.length === 0) {
      setUploadError('Please select a file to upload');
      return null;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');
    
    try {
      const mainFile = mediaFiles[0].file;
      const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB
      const isLargeFile = mainFile.size > LARGE_FILE_THRESHOLD;
      
      if (isLargeFile) {
        console.log(`File size ${(mainFile.size / (1024 * 1024)).toFixed(2)}MB exceeds threshold, using chunked upload`);
        const uploadedFile = await handleChunkedUpload(mainFile);
        if (!uploadedFile) return null;
        
        if (isCarousel && mediaFiles.length > 1) {
          const additionalFiles = [];
          for (let i = 1; i < mediaFiles.length; i++) {
            const file = mediaFiles[i].file;
            const additionalFile = await handleChunkedUpload(file);
            if (additionalFile) {
              additionalFiles.push({
                ...additionalFile,
                isMain: false,
                index: i
              });
            }
          }
          return {
            isCarousel: true,
            files: [uploadedFile, ...additionalFiles]
          };
        }
        
        return {
          isCarousel: false,
          files: [uploadedFile]
        };
      }
      
      // For smaller files, use the existing form data approach
      const formData = new FormData();
      formData.append('media', mainFile);
      formData.append('type', postType);
      
      // Add video thumbnail timestamp if applicable
      if (postType === 'video') {
        formData.append('thumbnailTimestamp', `${videoThumbnailTime}%`);
      }
      
      // Add trimming information if the file was trimmed
      if (mainFile.isTrimmed) {
        formData.append('trimStart', mainFile.trimStart.toString());
        formData.append('trimEnd', mainFile.trimEnd.toString());
        formData.append('originalDuration', mainFile.originalDuration.toString());
      }
      
      // Add audio cover image if present
      if (postType === 'audio' && audioCoverImage) {
        formData.append('audioCover', audioCoverImage.file);
      }
      
      // Add additional files for carousel
      if (isCarousel && mediaFiles.length > 1) {
        for (let i = 1; i < mediaFiles.length; i++) {
          formData.append(`carousel_${i}`, mediaFiles[i].file);
        }
      }
      
      // Upload progress tracking
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        }
      });
      
      const response = await new Promise((resolve, reject) => {
        xhr.open('POST', '/processAndUploadMedia', true);
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (error) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            reject(new Error(`Server returned ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error occurred'));
        xhr.send(formData);
      });
      
      return response;
    } catch (error) {
      console.error('Error processing and uploading media:', error);
      setUploadError(error.message || 'Failed to upload media');
      return null;
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Check if we have files to upload
      if (uploadMethod === 'upload' && mediaFiles.length === 0) {
        setUploadError('Please select a file to upload');
        return;
      }
      
      // Show uploading state
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError('');
      
      // Normalize tags
      const normalizedTags = [...new Set([
        ...selectedExistingTags,
        ...tags.split(',')
          .map(tag => tag.trim().toLowerCase().replace(/\s+/g, ''))
          .filter(tag => tag)
      ])];
      
      // Prepare additional data for processing
      let extraData = {};
      
      // Add trimming data if applicable
      if ((postType === 'video' || postType === 'audio') && 
          trimStart !== 0 && trimEnd !== mediaDuration) {
        extraData.trimStart = trimStart;
        extraData.trimEnd = trimEnd;
      }
      
    // Handle based on upload method
      if (postType === 'audio' && uploadMethod === 'embed') {
        // Check if we have embed data
        if (!embedData) {
          // Try to fetch embed data if URL is provided
          if (url) {
            await fetchOEmbedData();
            // Re-check embedData after fetching
            if (!embedData) {
              setUploadError('Failed to fetch embed data. Please check the URL and try again.');
              return;
            }
          } else {
            setUploadError('Please enter a valid URL');
            return;
          }
        }
        
        // Ensure we have the minimum required data
        if (!embedData.html) {
          setUploadError('Invalid embed data - missing required fields');
          return;
        }
        
        // Prepare post data for audio embed
        const postData = {
          type: postType,
          caption,
          tags: normalizedTags,
          // Only include link if user is Elite and a link is provided
          ...(userData?.membershipTier === 'Elite' && link ? { link } : {}),
          tags: normalizedTags,
          // Make sure all embedData fields have fallback values to avoid undefined
          embedData: {
            url: embedData.url || url,
            html: embedData.html,
            thumbnail_url: embedData.thumbnail_url || null,
            title: embedData.title || null,
            author_name: embedData.author_name || null,
            author_url: embedData.author_url || null,
            provider_name: embedData.provider_name || null,
            provider_url: embedData.provider_url || null
          },
          // Set this explicitly to null to prevent error
          mediaFiles: null
        };
        
        console.log('Submitting embed post with data:', postData);
        
        // Call the onUpload function provided by the parent component
        await onUpload(postData);
        onClose();
      } else {
        // For file uploads with processed media
        const processResult = await processAndUploadMedia(extraData);
        
        if (!processResult) {
          return; // Error occurred during upload
        }
        
        // Prepare post data for uploaded media
        const postData = {
          type: postType,
          caption,
          tags: normalizedTags,
          mediaFiles: processResult.isCarousel ? processResult.files : processResult.files[0],
          // Only include link if user is Elite and a link is provided
          ...(userData?.membershipTier === 'Elite' && link ? { link } : {})
        };
        
        await onUpload(postData);
        onClose();
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setUploadError(error.message || 'Failed to create post');
    } finally {
      setIsUploading(false);
    }
  };
  
  const resetForm = () => {
    if (!editingPost) {
      setPostType(null);
      setUrl('');
      setCaption('');
      setTags('');
      setSelectedExistingTags([]);
      setMediaFiles([]);
      setCurrentFileIndex(0);
      setVideoThumbnailTime(50);
      setIsCarousel(false);
      setEmbedData(null);
      setEmbedLoading(false);
      setEmbedError('');
      setIsUploading(false);
      setUploadProgress(0);
      setUploadError('');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleUploadMethodChange = (method) => {
    setUploadMethod(method);
    // Reset file and URL when switching methods
    if (method === 'embed') {
      setMediaFiles([]);
      setIsCarousel(false);
    } else {
      setUrl('');
      setEmbedData(null);
    }
  };

  // Handle file input click
  const handleFileInputClick = () => {
    fileInputRef.current.click();
  };
  
  // Handle carousel input click
  const handleCarouselInputClick = () => {
    carouselInputRef.current.click();
  };
  
  // Handle video seeking for thumbnail selection
  const handleVideoSeek = (e) => {
    if (videoRef.current) {
      const video = videoRef.current;
      const percent = parseInt(e.target.value, 10);
      setVideoThumbnailTime(percent);
      
      // Calculate time based on video duration and seek to it
      if (!isNaN(video.duration)) {
        const seekTime = (percent / 100) * video.duration;
        video.currentTime = seekTime;
      }
    }
  };

  if (!isOpen) return null;

  // Initial screen to select post type
  if (!postType && !editingPost) {
    return (
      <div className={styles['modal-overlay']}>
        <div className={styles['modal-content']}>
          <button className={styles['modal-close']} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className={styles['upload-options']}>
            <h2>Choose Upload Type</h2>
            <div className={styles['upload-buttons']}>
              <button onClick={() => setPostType('video')}>
                <FontAwesomeIcon icon={faVideo} />
                <span>Video</span>
              </button>
              <button onClick={() => setPostType('audio')}>
                <FontAwesomeIcon icon={faMusic} />
                <span>Audio</span>
              </button>
              <button onClick={() => setPostType('photo')}>
                <FontAwesomeIcon icon={faImage} />
                <span>Photo</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getFormTitle = () => {
    if (editingPost) {
      return 'Edit Post';
    } else if (postType) {
      return `${postType.charAt(0).toUpperCase() + postType.slice(1)} Upload`;
    }
    return 'Choose Upload Type';
  };

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <form onSubmit={handleSubmit} className={styles['upload-form']}>
          <h2>{getFormTitle()}</h2>
          
          {/* Only show upload method selection for audio */}
          {postType === 'audio' && !editingPost && (
            <div className={styles['upload-method']}>
              <div className={styles['method-tabs']}>
                <button 
                  type="button"
                  className={`${styles['method-tab']} ${uploadMethod === 'upload' ? styles['active'] : ''}`}
                  onClick={() => handleUploadMethodChange('upload')}
                >
                  Upload File
                </button>
                <button 
                  type="button"
                  className={`${styles['method-tab']} ${uploadMethod === 'embed' ? styles['active'] : ''}`}
                  onClick={() => handleUploadMethodChange('embed')}
                >
                  Embed Link
                </button>
              </div>
            </div>
          )}

          {postType === 'audio' && uploadMethod === 'upload' && (
            <div className={styles['audio-cover-section']}>
              <label>Audio Cover Image (Optional)</label>
              <input 
                type="file" 
                ref={audioCoverInputRef}
                onChange={handleAudioCoverChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              {audioCoverImage ? (
                <div className={styles['audio-cover-preview']}>
                  <img 
                    src={audioCoverImage.previewUrl} 
                    alt="Audio cover preview" 
                    className={styles['cover-preview-image']}
                  />
                  <button 
                    type="button"
                    className={styles['remove-cover']}
                    onClick={handleRemoveAudioCover}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <button 
                  type="button" 
                  onClick={handleAudioCoverInputClick}
                  className={styles['audio-cover-upload-button']}
                >
                  <FontAwesomeIcon icon={faImage} />
                  <span>Upload Cover Image</span>
                </button>
              )}
            </div>
          )}
          
          {/* Audio Embed UI */}
          {postType === 'audio' && uploadMethod === 'embed' && (
            <div className={styles['embed-container']}>
              <div className={styles['form-group']}>
                <label>Spotify or SoundCloud URL</label>
                <div className={styles['url-input-group']}>
                  <input 
                    type="url" 
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="Enter Spotify or SoundCloud URL"
                    disabled={embedLoading}
                    required
                  />
                  <button 
                    type="button" 
                    onClick={fetchOEmbedData}
                    className={styles['fetch-button']}
                    disabled={embedLoading || !url}
                  >
                    {embedLoading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <span>Fetch</span>
                    )}
                  </button>
                </div>
                <small>
                  Supported: Spotify tracks, SoundCloud tracks
                </small>
                
                {embedError && (
                  <div className={styles['embed-error']}>
                    {embedError}
                  </div>
                )}
              </div>
              
              {embedData && (
                <div className={styles['embed-preview']}>
                  <div className={styles['embed-info']}>
                    <h3>{embedData.title}</h3>
                    <p>{embedData.author_name}</p>
                    {embedData.thumbnail_url && (
                      <img 
                        src={embedData.thumbnail_url} 
                        alt={embedData.title}
                        className={styles['embed-thumbnail']}
                      />
                    )}
                  </div>
                  <div 
                    className={styles['embed-player']}
                    dangerouslySetInnerHTML={{ __html: embedData.html }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* File Upload UI (for all upload types) */}
          {(postType !== 'audio' || uploadMethod === 'upload') && (
            <div className={styles['upload-container']}>
              {/* File Input */}
              <div className={styles['form-group']}>
                <label>
                  {isCarousel ? 'Add Media' : `Upload ${postType.charAt(0).toUpperCase() + postType.slice(1)}`}
                </label>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept={
                    postType === 'photo' 
                      ? 'image/*' 
                      : postType === 'video' 
                        ? 'video/*' 
                        : postType === 'audio' 
                          ? 'audio/*' 
                          : '*'
                  }
                  style={{ display: 'none' }}
                />
                
                {/* Carousel file input */}
                {postType === 'photo' && (
                  <input 
                    type="file" 
                    ref={carouselInputRef}
                    onChange={handleCarouselFileChange}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                  />
                )}
                
                <div className={styles['upload-buttons-container']}>
                  <button 
                    type="button" 
                    onClick={handleFileInputClick}
                    className={styles['file-upload-button']}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faUpload} />
                    <span>
                      {mediaFiles.length === 0 
                        ? `Select ${postType} file` 
                        : 'Replace file'}
                    </span>
                  </button>
                  
                  {/* Show carousel button only for photos */}
                  {postType === 'photo' && (
                    <button 
                      type="button" 
                      onClick={handleCarouselInputClick}
                      className={styles['carousel-upload-button']}
                      disabled={isUploading}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add more photos</span>
                    </button>
                  )}
                </div>
                
                {/* Show selected files */}
                {mediaFiles.length > 0 && (
                  <div className={styles['selected-files']}>
                    {mediaFiles.map((file, index) => (
                      <div 
                        key={index} 
                        className={`${styles['file-item']} ${index === currentFileIndex ? styles['active'] : ''}`}
                        onClick={() => setCurrentFileIndex(index)}
                      >
                        <div className={styles['file-item-inner']}>
                          {file.type.startsWith('image/') && (
                            <img 
                              src={file.previewUrl} 
                              alt={file.name}
                              className={styles['file-preview']}
                            />
                          )}
                          {file.type.startsWith('video/') && (
                            <video 
                              src={file.previewUrl}
                              className={styles['file-preview']}
                            />
                          )}
                          {file.type.startsWith('audio/') && (
                            <div className={styles['audio-preview']}>
                              <FontAwesomeIcon icon={faMusic} />
                            </div>
                          )}
                        </div>
                        <button 
                          type="button"
                          className={styles['remove-file']}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected file preview */}
              {mediaFiles.length > 0 && (
                <div className={styles['media-preview']}>
                  {mediaFiles[currentFileIndex]?.type.startsWith('image/') && (
                    <img 
                      src={mediaFiles[currentFileIndex].previewUrl} 
                      alt={mediaFiles[currentFileIndex].name}
                      className={styles['preview-image']}
                    />
                  )}
                  
                  {mediaFiles[currentFileIndex]?.type.startsWith('video/') && (
                    <div className={styles['video-preview-container']}>
                      <video 
                        ref={videoRef}
                        src={mediaFiles[currentFileIndex].previewUrl}
                        className={styles['preview-video']}
                        controls
                      />
                      
                      {/* Video thumbnail selection */}
                      <div className={styles['thumbnail-selection']}>
                        <label>Select Thumbnail Frame:</label>
                        <input 
                          type="range" 
                          min="0" 
                          max="100" 
                          value={videoThumbnailTime}
                          onChange={handleVideoSeek}
                          className={styles['thumbnail-slider']}
                        />
                        <div className={styles['thumbnail-time']}>
                          {videoThumbnailTime}%
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {mediaFiles[currentFileIndex]?.type.startsWith('audio/') && (
                    <div className={styles['audio-preview-container']}>
                      <div className={styles['audio-preview-icon']}>
                        <FontAwesomeIcon icon={faMusic} size="3x" />
                      </div>
                      <audio 
                        src={mediaFiles[currentFileIndex].previewUrl}
                        controls
                        className={styles['preview-audio']}
                      />
                    </div>
                  )}
                </div>
              )}
              
              {isUploading && (
                <div className={styles['upload-progress']}>
                  <div 
                    className={styles['progress-bar']} 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                  <div className={styles['progress-text']}>
                    Uploading: {uploadProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          {uploadError && (
            <div className={styles['upload-error']}>
              {uploadError}
            </div>
          )}
          
          {/* Caption & Tags (for all post types) */}
          <div className={styles['form-group']}>
            <label>Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write a caption..."
              className={styles['caption-input']}
            />
          </div>

          <div className={styles['form-group']}>
            <label>Link {userData?.membershipTier !== 'Elite' && <span className={styles['upgrade-badge']}>Elite Feature</span>}</label>
            {userData?.membershipTier === 'Elite' ? (
              <input
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Add a link to your post (optional)"
                className={styles['link-input']}
              />
            ) : (
              <div className={styles['upgrade-link-container']}>
                <input
                  type="url"
                  disabled
                  placeholder="Add a link to your post (Elite members only)"
                  className={styles['link-input-disabled']}
                />
                <button 
                  type="button" 
                  className={styles['upgrade-button']}
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade to Elite
                </button>
              </div>
            )}
            <small className={styles['helper-text']}>
              Links allow viewers to visit your website, store, or other content
            </small>
          </div>

          <div className={styles['form-group']}>
            <label>Tags</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Add tags separated by commas (no spaces allowed)"
              className={styles['tags-input']}
            />
            <small className={styles['helper-text']}>
              Tags should be single words without spaces (e.g., newmusic, tourlife, behindthescenes)
            </small>
            <div className={styles['existing-tags']}>
              {existingTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={`${styles['tag-button']} ${
                    selectedExistingTags.includes(tag) ? styles['selected'] : ''
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Submit buttons */}
          <div className={styles['form-actions']}>
            <button 
              type="button" 
              onClick={() => editingPost ? onClose() : setPostType(null)}
              className={styles['secondary-button']}
              disabled={isUploading}
            >
              {editingPost ? 'Cancel' : 'Back'}
            </button>
            <button 
              type="submit"
              className={styles['primary-button']}
              disabled={isUploading || 
                (postType === 'audio' && uploadMethod === 'embed' && !embedData) ||
                (uploadMethod === 'upload' && mediaFiles.length === 0)}
            >
              {isUploading ? 'Uploading...' : (editingPost ? 'Update' : 'Upload')}
            </button>
          </div>
        </form>
      </div>
      
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          navigate={navigate}
        />
      )}

      {showDurationModal && (
        <DurationLimitModal
          isOpen={showDurationModal}
          onClose={() => setShowDurationModal(false)}
          mediaType={postType}
          duration={mediaDuration}
          maxDuration={getMaxDuration(postType, userData?.membershipTier || 'Free')}
        />
      )}

      {/* Audio Trimmer Modal */}
      {showTrimmer && postType === 'audio' && mediaFiles.length > 0 && (
        <AudioTrimmerModal
          isOpen={showTrimmer}
          onClose={() => setShowTrimmer(false)}
          onSave={(start, end, trimmedFile) => {
            setTrimStart(start);
            setTrimEnd(end);
            
            if (trimmedFile) {
              const updatedFiles = [...mediaFiles];
              if (updatedFiles[currentFileIndex]?.previewUrl) {
                URL.revokeObjectURL(updatedFiles[currentFileIndex].previewUrl);
              }
              updatedFiles[currentFileIndex] = {
                ...updatedFiles[currentFileIndex],
                file: trimmedFile,
                previewUrl: URL.createObjectURL(trimmedFile)
              };
              setMediaFiles(updatedFiles);
            }
            
            setShowTrimmer(false);
          }}
          file={mediaFiles[currentFileIndex]?.file}
          maxDuration={getMaxDuration('audio', userData?.membershipTier || 'Free')}
          originalDuration={mediaDuration}
        />
      )}

      {/* Video Trimmer Modal */}
      {showTrimmer && postType === 'video' && mediaFiles.length > 0 && (
        <VideoTrimmerModal
          isOpen={showTrimmer}
          onClose={() => setShowTrimmer(false)}
          onSave={(start, end, trimmedFile) => {
            setTrimStart(start);
            setTrimEnd(end);
            
            if (trimmedFile) {
              const updatedFiles = [...mediaFiles];
              if (updatedFiles[currentFileIndex]?.previewUrl) {
                URL.revokeObjectURL(updatedFiles[currentFileIndex].previewUrl);
              }
              updatedFiles[currentFileIndex] = {
                ...updatedFiles[currentFileIndex],
                file: trimmedFile,
                previewUrl: URL.createObjectURL(trimmedFile)
              };
              setMediaFiles(updatedFiles);
            }
            
            setShowTrimmer(false);
          }}
          file={mediaFiles[currentFileIndex]?.file}
          maxDuration={getMaxDuration('video', userData?.membershipTier || 'Free')}
          originalDuration={mediaDuration}
        />
      )}
    </div>
  );
};

export default UploadModal;